import { useState, useMemo } from "react";
import { ToolShell, NumberField } from "./parts";
import { inr, inrShort } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X, Trophy, AlertTriangle } from "lucide-react";
import { LeadDialog } from "@/components/LeadDialog";

const METRICS = [
  { key: "price", label: "Price", type: "money", wKey: null },
  { key: "area", label: "Carpet Area (sq.ft)", type: "num", wKey: "space", lower: false },
  { key: "ppsf", label: "Price / sq.ft", type: "derived", wKey: "price", lower: true },
  { key: "distance", label: "Distance to work (km)", type: "num", wKey: "location", lower: true },
  { key: "possession", label: "Possession (months, 0=ready)", type: "num", wKey: "possession", lower: true },
  { key: "amenities", label: "Amenities score (1-10)", type: "num", wKey: "amenities", lower: false },
];
const SCORED = METRICS.filter((m) => m.wKey);

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const blank = (i) => ({ id: uid(), name: `Property ${i + 1}`, price: 0, area: 0, distance: 0, possession: 0, amenities: 5 });

export function PropertyComparison() {
  const [items, setItems] = useState([blank(0), blank(1), blank(2)]);
  const [w, setW] = useState({ price: 8, space: 7, location: 6, possession: 5, amenities: 5 });

  const update = (id, key, val) => setItems(items.map((p) => (p.id === id ? { ...p, [key]: val } : p)));
  const add = () => items.length < 4 && setItems([...items, blank(items.length)]);
  const remove = (id) => items.length > 2 && setItems(items.filter((p) => p.id !== id));

  const { ranked, incomplete, bestId } = useMemo(() => {
    const derived = items.map((p) => ({ ...p, ppsf: p.area > 0 ? p.price / p.area : 0 }));
    const incomplete = derived.some((p) => !(p.price > 0 && p.area > 0));
    const range = {};
    SCORED.forEach((m) => {
      const vals = derived.map((p) => p[m.key]);
      range[m.key] = { min: Math.min(...vals), max: Math.max(...vals) };
    });
    const totalW = Object.values(w).reduce((a, b) => a + b, 0) || 1;
    const scored = derived.map((p) => {
      let s = 0;
      SCORED.forEach((m) => {
        const { min, max } = range[m.key];
        let n = max === min ? 0.5 : (p[m.key] - min) / (max - min);
        if (m.lower) n = 1 - n;
        s += (w[m.wKey] / totalW) * n;
      });
      return { ...p, score: Math.round(s * 100) };
    });
    const sorted = [...scored].sort((a, b) => b.score - a.score);
    return { ranked: scored, incomplete, bestId: sorted[0]?.id };
  }, [items, w]);

  const best = ranked.find((p) => p.id === bestId);

  const cell = (p, key, prefix) => (
    <Input
      type={key === "name" ? "text" : "number"}
      value={p[key]}
      onChange={(e) => update(p.id, key, key === "name" ? e.target.value : Number(e.target.value))}
      data-testid={`cmp-${key}-${p.id}`}
      className="tool-input h-9 rounded-none border-border bg-background text-center font-mono text-sm"
    />
  );

  return (
    <ToolShell title="Property Comparison" desc="Enter 2-4 shortlisted properties and set what matters most to you. Fill every field for the most accurate recommendation — we score each option and pick your best-fit home.">
      {/* priority weights */}
      <div className="mb-8 border border-border bg-background p-5">
        <p className="mb-5 font-mono text-xs uppercase tracking-widest text-muted-foreground">What matters most to you?</p>
        <div className="grid gap-6 md:grid-cols-2">
          <NumberField label="Low price" value={w.price} onChange={(v) => setW({ ...w, price: v })} min={0} max={10} step={1} suffix="/10" testid="cmp-w-price" />
          <NumberField label="More space" value={w.space} onChange={(v) => setW({ ...w, space: v })} min={0} max={10} step={1} suffix="/10" testid="cmp-w-space" />
          <NumberField label="Close to work" value={w.location} onChange={(v) => setW({ ...w, location: v })} min={0} max={10} step={1} suffix="/10" testid="cmp-w-location" />
          <NumberField label="Fast possession" value={w.possession} onChange={(v) => setW({ ...w, possession: v })} min={0} max={10} step={1} suffix="/10" testid="cmp-w-possession" />
          <NumberField label="Amenities" value={w.amenities} onChange={(v) => setW({ ...w, amenities: v })} min={0} max={10} step={1} suffix="/10" testid="cmp-w-amenities" />
        </div>
      </div>

      {/* comparison table */}
      <div className="overflow-x-auto border border-border" data-testid="cmp-table">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr className="border-b border-border bg-background">
              <th className="p-3 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Spec</th>
              {items.map((p) => (
                <th key={p.id} className={`p-3 ${p.id === bestId && !incomplete ? "bg-secondary" : ""}`}>
                  <div className="flex items-center gap-2">
                    {cell(p, "name")}
                    {items.length > 2 && (
                      <button onClick={() => remove(p.id)} data-testid={`cmp-remove-${p.id}`} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m) => (
              <tr key={m.key} className="border-b border-border">
                <td className="p-3 text-sm text-muted-foreground">{m.label}</td>
                {items.map((p) => (
                  <td key={p.id} className={`p-3 ${p.id === bestId && !incomplete ? "bg-secondary" : ""}`}>
                    {m.type === "derived"
                      ? <span className="block text-center font-mono text-sm text-foreground">{p.area > 0 ? inr(p.price / p.area) : "—"}</span>
                      : m.type === "money"
                        ? cell(p, "price")
                        : cell(p, m.key)}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-background">
              <td className="p-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Match score</td>
              {items.map((p) => (
                <td key={p.id} className={`p-3 text-center ${p.id === bestId && !incomplete ? "bg-secondary" : ""}`} data-testid={`cmp-score-${p.id}`}>
                  <span className={`font-mono text-2xl font-bold ${p.id === bestId && !incomplete ? "text-cyan" : "text-foreground"}`}>{incomplete ? "—" : `${p.score}`}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {items.length < 4 && (
        <Button variant="outline" onClick={add} data-testid="cmp-add" className="mt-4 rounded-none border-border font-mono text-xs uppercase tracking-widest hover:border-foreground">
          <Plus className="mr-2 h-4 w-4" /> Add property
        </Button>
      )}

      {/* recommendation */}
      <div className="mt-8">
        {incomplete ? (
          <div className="flex items-center gap-3 border border-border bg-background p-5" data-testid="cmp-incomplete">
            <AlertTriangle className="h-5 w-5 text-cyan" />
            <p className="text-sm text-muted-foreground">Enter a valid <b className="text-foreground">price</b> and <b className="text-foreground">carpet area</b> for every property to get your recommendation.</p>
          </div>
        ) : best && (
          <div className="border-2 border-foreground bg-secondary p-6" data-testid="cmp-recommendation">
            <div className="flex items-center gap-3">
              <Trophy className="h-6 w-6 text-cyan" />
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Best fit for your priorities</p>
            </div>
            <p className="mt-2 font-display text-3xl font-extrabold uppercase text-foreground" data-testid="cmp-winner">{best.name}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Match score {best.score}/100 · {inrShort(best.price)} · {best.area} sq.ft · {inr(best.price / best.area)}/sq.ft · {best.possession === 0 ? "ready to move" : `${best.possession} mo to possession`}
            </p>
            <LeadDialog source="property-comparison" defaultNote={`I'm comparing properties and leaning towards "${best.name}". Please help me evaluate.`} trigger={
              <Button className="mt-5 rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90" data-testid="cmp-lead-cta">
                Get Ayush's expert opinion
              </Button>
            } />
          </div>
        )}
      </div>
    </ToolShell>
  );
}
